import React, { useState, useEffect } from 'react';
import AppIcon from './AppIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import UserAvatar from './UserAvatar';
import EquipmentLoadout from './EquipmentLoadout';
import AvatarUploadModal from './AvatarUploadModal';

const EditProfileModal = ({ isOpen, onClose }) => {
  const { user, updateUser } = useAuth();
  
  const [name, setName] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState('char1');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setSelectedAvatar(user.avatarId || 'char1');
    }
  }, [user, isOpen]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updates = { name, avatarId: selectedAvatar };
      // 1. 本地更新
      await updateUser(updates);
      // 2. 数据库更新
      if (apiService.updateProfile) {
        await apiService.updateProfile(updates);
      }
      onClose();
    } catch (error) {
      console.error("Profile update failed", error);
      alert("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCustomUploadSuccess = async (downloadUrl) => {
    try {
      await updateUser({ customAvatarUrl: downloadUrl });
      if (apiService.updateProfile) {
        await apiService.updateProfile({ customAvatarUrl: downloadUrl });
      }
      setShowUploadModal(false);
    } catch (e) {
      console.error(e);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><AppIcon name="times" className="text-xl" /></button>
          
          <h3 className="text-2xl font-bold text-center mb-6">Edit Profile</h3>
          
          <div className="mb-6">
            <label className="block text-sm font-bold text-gray-600 mb-2 uppercase">Display Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 font-bold" />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-bold text-gray-600 mb-4 uppercase">Choose Avatar</label>
            <div className="grid grid-cols-5 gap-2 mb-4">
              {['char1', 'char2', 'char3', 'char4', 'char5'].map(char => (
                <button key={char} onClick={() => setSelectedAvatar(char)} className={`rounded-xl border-2 transition-all ${selectedAvatar === char ? 'border-purple-500 ring-2 ring-purple-200 scale-110 z-10' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                  <UserAvatar avatarId={char} inventory={[]} size="sm" className="mx-auto pointer-events-none" />
                </button>
              ))}
            </div>
            
            <button onClick={() => setShowUploadModal(true)} className="w-full py-2 border-2 border-dashed border-purple-300 text-purple-600 rounded-xl font-bold hover:bg-purple-50 flex items-center justify-center gap-2 text-sm">
                <AppIcon name="camera" /> Upload Custom Photo
            </button>
          </div>

          <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 mb-6">
             <div className="flex items-center gap-4">
                <UserAvatar avatarId={selectedAvatar} customAvatarUrl={user?.customAvatarUrl} inventory={user?.inventory || []} size="md" />
                <div>
                    <div className="text-xs text-gray-500 font-bold uppercase">Preview</div>
                    <div className="font-bold text-gray-800 text-lg">{name}</div>
                </div>
             </div>
             {/* 🟢 [方案B] 装备架：已拥有装备以 chip 形式展示，不再叠加到头像上 */}
             <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-xs text-gray-500 font-bold uppercase mb-2">My Gear</div>
                <EquipmentLoadout inventory={user?.inventory || []} />
             </div>
          </div>

          <button onClick={handleSave} disabled={isSaving} className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3.5 rounded-xl shadow-lg disabled:opacity-70">
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </motion.div>
      </div>

      <AnimatePresence>
        {showUploadModal && <AvatarUploadModal onClose={() => setShowUploadModal(false)} onUploadSuccess={handleCustomUploadSuccess} />}
      </AnimatePresence>
    </>
  );
};

export default EditProfileModal;